import { useEffect, useMemo, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { IListagemUsuario, userService } from "../../../shared/services/api/user/userService";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { useDebounce } from "../../../shared/hooks";
import { checkByProfileGuard, ProfileGuard } from '../../../shared/guards/ProfileGuard';
import { PermissionActionsEnum, PermissionRolesEnum } from '../../../shared/enums/profiles';
import { ListSearchParams } from '../../analysis/types/list-search-params.type';
import moment from 'moment/moment';
import { useProfile } from '../../../context/ProfileContext';
import { useToast } from '../../../shared/hooks';
import { ToastContent } from '../../../shared/components';
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { TableUsers } from "./TableUsers";
import { ToolbarUsers } from "./ToolbarUsers";


function translate(traduzir: String) {

  if (traduzir === 'estate_admin') { return 'Admin Imobiliária' }
  if (traduzir === 'admin') { return 'Administrador' }

  return traduzir;

};


export const ListagemUsuariosPresentation: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const [rows, setRows] = useState<IListagemUsuario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const hasViewPermission = useMemo(() => {
    return checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.View)
  }, [])

  const canFilterUsers = useMemo(() => {
    return checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Filter)
  }, [])

  const canFilterPermissions = useMemo(() => {
    return checkByProfileGuard(PermissionRolesEnum.PermissionsOptions, PermissionActionsEnum.Filter)
  }, [])

  const canAddUser = useMemo(() => {
    return checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Add)
  }, [])
  const toastHandler = useToast();
  const {
    userClientId,
    isSuperAdmin,
    permissionLevel,
    permissionId,
    permissionDescription,
    registerClient,
    isPermissionInitialized,
  } = useProfile();

  // Enviamos o mesmo perfil de antes para o back, MENOS o mapa `permissions`.
  // `permissions` e um Record de 46 roles x 40 acoes: serializado e encodado dava ~56KB
  // na query string e estourava o limite do servidor (414 - request URI too large).
  // Sem ele o param fica em ~1.4KB. Os demais campos seguem identicos ao que o back ja recebia.
  const userLoggedParam = useMemo(() => {
    return JSON.stringify({
      registerClient,
      permissionLevel,
      permissionId,
      isPermissionInitialized,
      isSuperAdmin,
      userClientId,
      permissionDescription,
    });
  }, [
    registerClient,
    permissionLevel,
    permissionId,
    isPermissionInitialized,
    isSuperAdmin,
    userClientId,
    permissionDescription,
  ]);

  const filterInitialParams: ListSearchParams = useMemo(() => {
    return {
      status: 'Todos',
      origem: 'Todos',
      dataIni: moment().startOf('month').subtract(24, 'months').format("YYYY-MM-DD"),
      dataFim: moment().endOf('month').format("YYYY-MM-DD")
    }
  }, [])

  useEffect(() => {
    const currentRowsPerPage = searchParams.get('rowsPerPage');
    setSearchParams({
      ...searchParams,
      dataini: filterInitialParams.dataIni,
      datafim: filterInitialParams.dataFim,
      status: filterInitialParams.status,
      rowsPerPage: currentRowsPerPage ?? '10',
    }, { replace: true })
  }, [])

  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const [buscaDraft, setBuscaDraft] = useState("");

  useEffect(() => {
    setBuscaDraft(busca);
  }, [busca]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  const rowsPerPage = useMemo(() => {
    return Number(searchParams.get('rowsPerPage') || '10');
  }, [searchParams]);

  const tipoUser = useMemo(() => {
    return (searchParams.get('tipo') || '');
  }, [searchParams]);

  const statusUser = useMemo(() => {
    return (searchParams.get('status') || '');
  }, [searchParams]);

  const clientId = useMemo(() => {
    const idClient = searchParams.get('clientId');
    const userClient = (isSuperAdmin || permissionLevel == 2) ? '' : userClientId;

    return idClient ? idClient : userClient;
  }, [searchParams]);


  const permissaoDescricao = useMemo(() => {
    const permissao = searchParams.get('permissaoDescricao');

    return permissao || '';
  }, [searchParams]);

  const updateParams = (next: {
    busca?: string;
    pagina?: string;
    tipo?: string;
    status?: string;
    clientId?: string;
    permissaoDescricao?: string;
    rowsPerPage?: string;
  }) => {
    setSearchParams(
      {
        busca,
        pagina: String(pagina),
        tipo: tipoUser,
        status: statusUser,
        clientId,
        permissaoDescricao,
        rowsPerPage: String(rowsPerPage),
        ...next,
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (hasViewPermission) {
      setIsLoading(true);
      debounce(() => {
        userService.getAll(pagina, busca, tipoUser, statusUser, clientId, permissaoDescricao, userLoggedParam, rowsPerPage)
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              const titleMessage = "Alerta!!";
              const contentMessage = "Erro: " + result?.message;

              toastHandler.present({
                type: 'error',
                position: 'top-right',
                messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
              })
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          }).catch((err: any) => {

            setIsLoading(false);
            const { titleMessage, contentMessage } = returnMessageError(err);

            toastHandler.present({
              type: 'error',
              position: 'top-right',
              messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
            })
          });
      });
    }
  }, [busca, pagina, tipoUser, statusUser, clientId, permissaoDescricao, rowsPerPage, userLoggedParam]);


  return (
    <LayoutBaseDePagina
      titulo="Usuários"
      subtitulo="Listagem de usuários"
      barraDeFerramentas={
        <ToolbarUsers
          busca={buscaDraft}
          showSearch={hasViewPermission}
          onChangeBusca={(texto) => setBuscaDraft(texto)}
          onSearch={() => updateParams({ busca: buscaDraft, pagina: '1' })}
          onClearSearch={() => {
            setBuscaDraft('');
            updateParams({ busca: '', pagina: '1' });
          }}
          textoButtonNew="novo"
          onClickNovo={() => navigate('/usuarios/detalhe/novo')}
          showButtonNew={hasViewPermission}
          isButtonNewDisabled={!canAddUser}
          statusValue={statusUser}
          showInputStatus={hasViewPermission}
          onChangeStatus={(retorno) => updateParams({ status: retorno })}
          clientIdValue={clientId}
          showInputClientFilter={hasViewPermission}
          onChangeClientFilter={(retorno) => updateParams({ clientId: retorno })}
          permissionDescriptionValue={permissaoDescricao}
		  showInputPermissionFilter={permissionLevel != 10}
		  isPermissionFilterDisabled={!(canFilterUsers && canFilterPermissions)}
          onChangePermissionFilter={(retornoPermissao) => updateParams({ permissaoDescricao: retornoPermissao })}
        />
      }
    >

      <TableUsers
        rows={rows}
        isLoading={isLoading}
        totalCount={totalCount}
        pagina={pagina}
        rowsPerPage={rowsPerPage}
        onChangePage={(newPage) => updateParams({ pagina: newPage.toString() })}
        onChangeRowsPerPage={(newRowsPerPage) => updateParams({ pagina: '1', rowsPerPage: newRowsPerPage.toString() })}
        onEdit={(userId) => navigate(`/usuarios/detalhe/${userId}`)}
      />
    </LayoutBaseDePagina >
  );
};